import React from 'react';
import { Link } from 'react-router-dom';
import { Github, GraduationCap, Users, Lock, Target, Wrench, ReceiptText, ArrowRight, Globe } from 'lucide-react';

const Projects = () => {
  const platforms = [
    {
      title: "Automated Invoice Generation System",
      badge: "Full-stack • Live",
      icon: <ReceiptText className="text-brand-primary" size={22} />,
      problem: "Invoicing contractors across departments meant opening Word templates, logging into Redmine or Jira, copying and translating tasks, then calculating hours, totals and amounts-in-words by hand — up to 20 minutes per invoice, with contractor data scattered across spreadsheets and frequent copy/paste and calculation errors.",
      users: "Finance and operations teams managing 100+ contractors across multiple departments",
      role: "End-to-end, from idea to delivery: workflow design, UX/UI, requirements, full-stack build, API integrations and AI features, testing and deployment (built by me with AI agents)",
      capabilities: ["Centralised contractor database", "Redmine & Jira integration", "AI template detection (Vision)", "EN↔UA translation", "DOCX & PDF generation", "Revenue dashboard"],
      security: "JWT-based authentication with session management to protect sensitive financial and contractor data; role-secured access.",
      outcome: "Cut invoice preparation from ~20 minutes to under 2 minutes (90% faster), eliminated manual data entry, centralised 100+ contractors into a single source of truth and significantly reduced errors.",
      tech: ["React", "FastAPI", "MongoDB", "JWT", "Redmine/Jira API", "python-docx", "OpenAI GPT & Vision"],
      link: null,
      caseStudyPath: "/case-studies/invoice-system",
      note: "Private company system — contains personal data, public login not available"
    },
    {
      title: "Domain Expiry Watcher",
      badge: "Full-stack • Live",
      icon: <Globe className="text-brand-primary" size={22} />,
      problem: "Domain portfolios were tracked manually in Excel with easily-missed calendar reminders — no centralised view, outdated data and a real risk of missed renewals leading to downtime, lost SEO and domains grabbed by squatters.",
      users: "IT admins and teams managing multiple domains across different registrars",
      role: "End-to-end, from idea to delivery: product design, UX/UI, full-stack build, WHOIS and email integrations, testing and deployment (built by me with AI agents)",
      capabilities: ["Real-time WHOIS lookups", "Colour-coded expiry dashboard", "CSV bulk import", "Email alerts (30/14/7 days)", "Scheduled auto-refresh", "Role-based team access"],
      security: "Authentication with role-based access and admin user management for teams.",
      outcome: "Replaces manual Excel tracking with automated monitoring and proactive alerts — reducing domain-management overhead by around 90% and bringing expiry risk close to zero.",
      tech: ["React", "FastAPI", "MongoDB Atlas", "WhoisXML API", "SendGrid", "Netlify / Render"],
      link: null,
      caseStudyPath: "/case-studies/domain-watcher"
    },
    {
      title: "Corporate Training & Compliance Platform",
      badge: "Full-stack • Live",
      icon: <GraduationCap className="text-brand-primary" size={22} />,
      problem: "The organisation had no centralised training system — onboarding and compliance training relied on PDFs, email chains and in-person sessions, with no tracking of completion or scores. ISO 27001 evidence was kept in spreadsheets, making audits manual and error-prone.",
      users: "All employees, plus HR/admins — for ISO 27001 security-awareness and compliance training",
      role: "End-to-end, from idea to delivery: workflow design, UX/UI, requirements, full-stack build, SendGrid email, PDF certificates, testing and deployment (built by me with AI agents)",
      capabilities: ["Multi-course (InfoSec, GDPR, Phishing)", "Interactive module-based learning", "Bilingual EN/UA", "Final quiz + scoring", "PDF certificates (1-yr validity)", "ISO-ready CSV reporting"],
      security: "JWT auth with role-based access (admin/employee); auditable training records and certificate-validity tracking for ISO 27001 evidence.",
      outcome: "Digitised the whole training lifecycle from assignment to certification with full audit trails — auditable proof of completion for ISO 27001, with automated reminders and bulk import removing manual HR coordination.",
      tech: ["React", "FastAPI", "MongoDB Atlas", "SendGrid", "JWT", "Server-side PDF"],
      link: null,
      caseStudyPath: "/case-studies/training-platform",
      note: "Private company system — contains personal data, public login not available"
    },
    {
      title: "HR & Organisation Management Platform",
      badge: "Full-stack • Live",
      icon: <Users className="text-brand-primary" size={22} />,
      problem: "HR and organisation data was scattered across spreadsheets, email, a third-party leave tool (Absentify) and a separate training system — no single source of truth, no live org view, painful multi-document updates and invisible access rights (a direct ISO 27001/audit risk).",
      users: "HR, department heads, executives and employees — with role-based access",
      role: "End-to-end, from idea to delivery: workflow design, UX/UI, requirements, modular full-stack build, integrations, RBAC/security, testing and deployment (built by me with AI agents)",
      capabilities: ["Live, editable org chart", "Employee & contractor profiles", "Systems & access register (ISO 27001)", "Leave management + team calendar", "RACI matrix & compliance", "Training & payroll modules"],
      security: "JWT auth + bcrypt hashing with role-based access control (Superadmin / Admin / Department Head / Employee); centralised access register, audit logs and documents in GridFS.",
      outcome: "A single source of truth for the whole employee lifecycle and ISO 27001 compliance — always-current org charts, one-action transfers/promotions with history, and audit-ready screening, access and RACI records.",
      tech: ["React", "FastAPI", "MongoDB + GridFS", "JWT", "bcrypt", "httpx", "REST + RBAC"],
      link: null,
      caseStudyPath: "/case-studies/hr-platform",
      note: "Private company system — contains personal data, public login not available"
    }
  ];

  return (
    <section id="projects" className="py-24 bg-bg-page" data-testid="projects-section">
      <div className="container mx-auto px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="heading-2 text-brand-primary mb-6">Digital Products & Platforms</h2>
            <p className="body-large max-w-3xl mx-auto">
              Alongside product and transformation delivery, I design and build practical internal digital tools using AI-assisted development. My focus is on solving real operational problems with usable, secure and maintainable solutions.
            </p>
          </div>

          {/* Platform Cards */}
          <div className="grid lg:grid-cols-2 gap-8">
            {platforms.map((platform, index) => (
              <div key={index} className="team-card p-8 flex flex-col" data-testid={`platform-card-${index}`}>
                <div className="flex items-center flex-wrap gap-3 mb-6">
                  {platform.icon}
                  <h3 className="heading-5 text-text-primary">{platform.title}</h3>
                  {platform.badge && (
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-soft text-brand-primary border border-indigo-100">
                      {platform.badge}
                    </span>
                  )}
                </div>

                <div className="space-y-5 flex-1">
                  <div>
                    <h4 className="caption text-brand-primary uppercase tracking-wide mb-1">Problem</h4>
                    <p className="body-small text-text-primary">{platform.problem}</p>
                  </div>
                  <div>
                    <h4 className="caption text-brand-primary uppercase tracking-wide mb-1">Intended Users</h4>
                    <p className="body-small text-text-primary">{platform.users}</p>
                  </div>
                  <div>
                    <h4 className="caption text-brand-primary uppercase tracking-wide mb-1">My Role</h4>
                    <p className="body-small text-text-primary">{platform.role}</p>
                  </div>
                  <div>
                    <h4 className="caption text-brand-primary uppercase tracking-wide mb-2">Key Capabilities</h4>
                    <div className="flex flex-wrap gap-2">
                      {platform.capabilities.map((cap, i) => (
                        <span key={i} className="px-3 py-1 bg-brand-soft text-brand-primary border border-indigo-100 rounded-full text-xs font-medium">
                          {cap}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Lock size={15} className="text-brand-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="caption text-brand-primary uppercase tracking-wide mb-1">Security & Privacy</h4>
                      <p className="body-small text-text-secondary">{platform.security}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <Target size={15} className="text-brand-primary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="caption text-brand-primary uppercase tracking-wide mb-1">Business Outcome</h4>
                      <p className="body-small text-text-secondary italic">{platform.outcome}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-border-light">
                  <div className="flex items-center space-x-2 mb-4">
                    <Wrench size={15} className="text-text-secondary" />
                    <div className="flex flex-wrap gap-2">
                      {platform.tech.map((t, i) => (
                        <span key={i} className="text-xs text-text-secondary">{t}{i < platform.tech.length - 1 ? ' •' : ''}</span>
                      ))}
                    </div>
                  </div>
                  {platform.note && (
                    <div className="flex items-center gap-1.5 mb-3 text-xs text-text-muted">
                      <Lock size={13} />
                      <span>{platform.note}</span>
                    </div>
                  )}
                  {platform.caseStudyPath ? (
                    <Link
                      to={platform.caseStudyPath}
                      className="inline-flex items-center gap-2 caption text-brand-primary hover:text-brand-hover transition-colors"
                      data-testid={`platform-casestudy-${index}`}
                    >
                      <span>View full case study</span>
                      <ArrowRight size={16} />
                    </Link>
                  ) : platform.link ? (
                    <a href={platform.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 caption text-brand-primary hover:text-brand-hover transition-colors">
                      <Github size={16} />
                      <span>View case study</span>
                    </a>
                  ) : (
                    <span className="inline-flex items-center space-x-2 caption text-text-muted">
                      <Github size={16} />
                      <span>Sanitised case study available on request</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Note */}
          <div className="mt-10 p-4 bg-bg-card border border-border-medium rounded-lg" data-testid="platforms-privacy-note">
            <p className="caption text-text-secondary text-center">
              Confidential company information, internal URLs, personal data, credentials and proprietary source code are not published. Case studies are sanitised where required.
            </p>
          </div>

          {/* Call to Action */}
          <div className="text-center mt-16">
            <div className="p-8 team-card">
              <h3 className="heading-4 text-text-primary mb-4">Interested in Learning More?</h3>
              <p className="body-medium mb-6 max-w-2xl mx-auto">
                I would be happy to discuss any of these platforms or transformation programmes in more detail, and how my experience can support your organisation.
              </p>
              <button 
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-primary"
                data-testid="platforms-discuss-btn"
              >
                Start a Conversation
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;
